import React, { useEffect, useMemo, useRef, useState } from 'react';
import { lessonRunsApi } from '../../../services/apiV2';
import { StudentSimulationState } from '../../../types/v2';
import { getSimulationSceneRenderer } from './renderers/registry';
import { acceptSimulationState, createClientActionId } from './simulationState';
import './SimulationActivity.css';

interface SimulationActivityProps {
  runId: number;
  stepId: number;
  simulation: StudentSimulationState;
  onStateAccepted?: (state: StudentSimulationState) => void;
  submitActionOverride?: (
    actionId: string,
    expectedRevision: number,
    clientActionId: string,
  ) => Promise<{ simulation?: StudentSimulationState }>;
}

export const SimulationActivity: React.FC<SimulationActivityProps> = ({
  runId,
  stepId,
  simulation,
  onStateAccepted,
  submitActionOverride,
}) => {
  const [localState, setLocalState] = useState<StudentSimulationState>(simulation);
  const [submittingActionId, setSubmittingActionId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const pendingClientIdRef = useRef<Record<string, string>>({});
  const mountedRef = useRef(true);
  const identity = `${runId}:${stepId}:${simulation.case_id}`;
  const identityRef = useRef(identity);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (identityRef.current !== identity) {
      identityRef.current = identity;
      pendingClientIdRef.current = {};
      setSubmittingActionId(null);
      setError('');
      setLocalState(simulation);
      return;
    }
    setLocalState((current) => acceptSimulationState(current, simulation) || current);
  }, [identity, simulation]);

  const actionLabels = useMemo(() => (
    Object.fromEntries(localState.presentation.actions.map((action) => [action.id, action.label]))
  ), [localState.presentation.actions]);
  const sceneActionIds = useMemo(() => {
    const ids = new Set<string>();
    for (const interaction of localState.presentation.interactions || []) {
      if (interaction.kind === 'hotspot') {
        ids.add(interaction.action_id);
      } else {
        interaction.targets.forEach((target) => ids.add(target.action_id));
      }
    }
    return ids;
  }, [localState.presentation.interactions]);
  const fallbackActions = localState.presentation.actions.filter(
    (action) => !sceneActionIds.has(action.id),
  );
  const SceneRenderer = getSimulationSceneRenderer(localState.presentation.renderer);
  const completed = localState.status === 'completed';

  const submitAction = async (actionId: string) => {
    if (submittingActionId || completed) return;
    const requestIdentity = identityRef.current;
    const clientActionId = pendingClientIdRef.current[actionId] || createClientActionId();
    pendingClientIdRef.current[actionId] = clientActionId;
    setSubmittingActionId(actionId);
    setError('');
    try {
      const payload = submitActionOverride
        ? await submitActionOverride(actionId, localState.revision, clientActionId)
        : await lessonRunsApi.submitSimulationAction(runId, {
          step_id: stepId,
          action_id: actionId,
          expected_revision: localState.revision,
          client_action_id: clientActionId,
        });
      const incoming = payload?.simulation as StudentSimulationState | undefined;
      if (!mountedRef.current || identityRef.current !== requestIdentity) return;
      setLocalState((current) => {
        const accepted = acceptSimulationState(current, incoming) || current;
        if (accepted !== current) onStateAccepted?.(accepted);
        return accepted;
      });
      delete pendingClientIdRef.current[actionId];
    } catch (err: any) {
      if (!mountedRef.current || identityRef.current !== requestIdentity) return;
      const incoming = err?.response?.data?.simulation as StudentSimulationState | undefined;
      if (incoming) {
        setLocalState((current) => {
          const accepted = acceptSimulationState(current, incoming) || current;
          if (accepted !== current) onStateAccepted?.(accepted);
          return accepted;
        });
      }
      setError(err?.response?.data?.error || 'Could not apply that simulation action.');
    } finally {
      if (mountedRef.current && identityRef.current === requestIdentity) {
        setSubmittingActionId(null);
      }
    }
  };

  return (
    <section className="simulation-activity mt-6 space-y-5" aria-label="Simulation activity">
      <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">Simulation</p>
        <p className="mt-1 text-sm text-sky-950">{localState.presentation.objective}</p>
        <p className="mt-2 text-xs text-sky-800">
          Phase: <span className="font-semibold capitalize">{localState.phase}</span>
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-900">How to finish this lab</h3>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-slate-700">
          <li>Read the mission and identify the problem you are investigating.</li>
          <li>Use the available controls to gather useful clues instead of guessing.</li>
          <li>Watch the <strong>Evidence captured</strong> area and use those clues to choose a safe next move.</li>
          <li>Verify the result after you make a supported change or test.</li>
          <li>You are finished only when this page says <strong>Simulation complete</strong>.</li>
        </ol>
        <p className="mt-2 text-xs text-slate-500">
          If you are stuck, use the hint control when one is available. A blocked or unhelpful action is feedback — use the evidence and try a better-supported next step.
        </p>
      </div>

      {SceneRenderer ? (
        <SceneRenderer
          key={identity}
          renderState={localState.render_state}
          interactions={localState.presentation.interactions}
          disabled={Boolean(submittingActionId) || completed}
          onAction={(actionId) => void submitAction(actionId)}
        />
      ) : (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          This simulation visual is unavailable.
        </div>
      )}

      {localState.safety_flag ? (
        <div role="alert" className="rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-900">
          Safety flag: an unsafe action was blocked and recorded.
        </div>
      ) : null}

      {localState.last_result ? (
        <div role="status" aria-live="polite" className={`rounded-xl border px-4 py-3 text-sm ${
          localState.last_result.unsafe
            ? 'border-rose-200 bg-rose-50 text-rose-900'
            : 'border-slate-200 bg-slate-50 text-slate-800'
        }`}>
          {localState.last_result.message}
        </div>
      ) : null}

      {localState.evidence_captured.length > 0 ? (
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Evidence captured</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {localState.evidence_captured.map((item) => (
              <span key={item.id} className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800">
                {item.label}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {completed ? (
        <div role="status" aria-live="polite" className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-4 text-emerald-950">
          <p className="font-semibold">Simulation complete</p>
          <p className="mt-1 text-sm">The required checks were verified. Wait for your teacher to advance the lesson.</p>
        </div>
      ) : (
        fallbackActions.length > 0 ? (
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              {sceneActionIds.size > 0 ? 'Other available controls' : 'Choose your next move'}
            </h3>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {fallbackActions.map((action) => (
              <button
                key={action.id}
                type="button"
                onClick={() => void submitAction(action.id)}
                disabled={Boolean(submittingActionId)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-left text-sm font-medium text-slate-800 transition hover:border-sky-400 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submittingActionId === action.id ? 'Applying…' : (actionLabels[action.id] || action.label)}
              </button>
              ))}
            </div>
          </div>
        ) : null
      )}

      {error ? (
        <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </div>
      ) : null}
    </section>
  );
};
